import {
  Goal,
  GroupWinner,
  MatchStats, Scores, ScoringRules, TeamResults, UserTeams,
} from '../types';

const initialTeamResults: TeamResults = {
  wins: 0,
  draws: 0,
  redCards: 0,
  cleanSheets: 0,
  ownGoals: 0,
  teamGoals3plus: 0,
};

export function calculateResults(
  userTeams: UserTeams[],
  matchStats: MatchStats[]
): { [key: string]: TeamResults } {
  const teamResultsDict = userTeams.reduce<{ [key: string]: TeamResults }>((acc, userTeam) => {
    acc[userTeam.team1Id] = { ...initialTeamResults };
    acc[userTeam.team2Id] = { ...initialTeamResults };
    return acc;
  }, {});

  matchStats.forEach((match) => {
    const homeTeam = match.statistics[0];
    const awayTeam = match.statistics[1];

    if (homeTeam && awayTeam) {
      const homeTeamId = homeTeam.id.toString();
      const awayTeamId = awayTeam.id.toString();

      const homeInDict = !!teamResultsDict[homeTeamId];
      const awayInDict = !!teamResultsDict[awayTeamId];
      if (!homeInDict && !awayInDict) return;

      const homeScore = match.score.home;
      const awayScore = match.score.away;

      if (homeScore > awayScore) {
        if (homeInDict) teamResultsDict[homeTeamId].wins += 1;
      } else if (homeScore < awayScore) {
        if (awayInDict) teamResultsDict[awayTeamId].wins += 1;
      } else if (homeScore === awayScore) {
        if (match.homeWinner) {
          if (homeInDict) teamResultsDict[homeTeamId].wins += 1;
        } else if (match.awayWinner) {
          if (awayInDict) teamResultsDict[awayTeamId].wins += 1;
        } else {
          if (homeInDict) teamResultsDict[homeTeamId].draws += 1;
          if (awayInDict) teamResultsDict[awayTeamId].draws += 1;
        }
      }

      const homeReds = homeTeam.redCards ?? 0;
      const awayReds = awayTeam.redCards ?? 0;
      if (homeInDict) teamResultsDict[homeTeamId].redCards += homeReds;
      if (awayInDict) teamResultsDict[awayTeamId].redCards += awayReds;

      if (homeScore === 0 && awayInDict) teamResultsDict[awayTeamId].cleanSheets += 1;
      if (awayScore === 0 && homeInDict) teamResultsDict[homeTeamId].cleanSheets += 1;

      if (homeScore >= 3 && homeInDict) teamResultsDict[homeTeamId].teamGoals3plus += 1;
      if (awayScore >= 3 && awayInDict) teamResultsDict[awayTeamId].teamGoals3plus += 1;

      match.goalEvents.forEach((goal) => {
        if (goal.type === 'Own Goal') {
          if (goal.team.toString() === homeTeamId && awayInDict) {
            teamResultsDict[awayTeamId].ownGoals += 1;
          }
          if (goal.team.toString() === awayTeamId && homeInDict) {
            teamResultsDict[homeTeamId].ownGoals += 1;
          }
        }
      });
    }
  });

  return teamResultsDict;
}

function countHattricks(teamId: string, matchStats: MatchStats[]): number {
  return matchStats.reduce((total, match) => {
    const goalsByPlayer: { [player: string]: number } = {};
    match.goalEvents.forEach((goal) => {
      if (goal.team.toString() === teamId && goal.type !== 'Own Goal' && goal.type !== 'Missed Penalty') {
        goalsByPlayer[goal.playerName] = (goalsByPlayer[goal.playerName] ?? 0) + 1;
      }
    });
    const hattricksInMatch = Object.values(goalsByPlayer).filter((g) => g >= 3).length;
    return total + hattricksInMatch;
  }, 0);
}

export function goalKey(matchId: number, goal: Goal): string {
  return `${matchId}_${goal.playerName}_${goal.time}_${goal.extra}`;
}

export function goalTotalSeconds(
  matchId: number,
  goal: Goal,
  secondsMap: Record<string, number>
): number {
  const key = goalKey(matchId, goal);
  return secondsMap[key] ?? (goal.time + (goal.extra ?? 0)) * 60;
}

// For "latest goal" comparisons: any added-time goal beats a non-added-time goal,
// and among added-time goals the one with more added minutes is later.
// This prevents 120+5 from beating 90+12 just because 120 > 90.
export function goalLatestSortKey(goal: Goal): number {
  return goal.extra != null ? 1_000_000 + goal.extra : goal.time;
}

function countSpecialGoals(
  teamIds: string[],
  matchStats: MatchStats[],
  goalKeys: string[]
): number {
  return matchStats.reduce((total, match) => {
    const teamGoals = match.goalEvents.filter(
      (g) => teamIds.includes(g.team.toString())
    );
    return total + teamGoals.filter(
      (g) => goalKeys.includes(goalKey(match.id, g))
    ).length;
  }, 0);
}

export function getMostRedsWinnerIds(
  teamResults: { [key: string]: TeamResults },
  userTeams: UserTeams[]
): Set<string> {
  const allTeamIds = Array.from(new Set(userTeams.flatMap((u) => [u.team1Id, u.team2Id])));
  const maxReds = Math.max(...allTeamIds.map((id) => teamResults[id]?.redCards ?? 0));
  if (maxReds < 2) return new Set();
  const topTeams = allTeamIds.filter((id) => (teamResults[id]?.redCards ?? 0) === maxReds);
  return topTeams.length === 1 ? new Set(topTeams) : new Set();
}

export function calculateScores(
  teamResults: { [key: string]: TeamResults },
  userTeams: UserTeams[],
  scoringRules: ScoringRules,
  topScorerTeam?: string,
  groupWinners?: GroupWinner[],
  earliestGoal?: Goal,
  latestGoal?: Goal,
  matchStats?: MatchStats[],
  directFkGoalKeys?: string[],
  olimpicoGoalKeys?: string[],
  keeperGoalKeys?: string[],
  mostRedsWinnerIds?: Set<string>
): Scores[] {
  const effectiveMostRedsWinnerIds = mostRedsWinnerIds ?? new Set<string>();

  return userTeams.map((user) => {
    const t1Results = teamResults[user.team1Id];
    const t2Results = teamResults[user.team2Id];
    const t1Id = user.team1Id;
    const t2Id = user.team2Id;

    const t1Score = t1Results.wins * scoringRules.t1Win
      + t1Results.draws * scoringRules.t1Draw
      + t1Results.cleanSheets * scoringRules.cleanSheet
      + t1Results.redCards * scoringRules.redCard
      + t1Results.ownGoals * scoringRules.ownGoal;

    const t2Score = t2Results.wins * scoringRules.t2Win
      + t2Results.draws * scoringRules.t2Draw
      + t2Results.cleanSheets * scoringRules.cleanSheet
      + t2Results.redCards * scoringRules.redCard
      + t2Results.ownGoals * scoringRules.ownGoal;

    const goldenBootWinner = t1Id === topScorerTeam?.toString()
      || t2Id === topScorerTeam?.toString();

    const t1GroupWinner = groupWinners?.some((w) => w.teamId.toString() === t1Id);
    const t2GroupWinner = groupWinners?.some((w) => w.teamId.toString() === t2Id);
    const groupWinnerScore = (t1GroupWinner ? scoringRules.t1WinGroup : 0)
      + (t2GroupWinner ? scoringRules.t2WinGroup : 0);

    const scoredEarliestGoal = t1Id === earliestGoal?.team.toString()
      || t2Id === earliestGoal?.team.toString();
    const scoredLatestGoal = t1Id === latestGoal?.team.toString()
      || t2Id === latestGoal?.team.toString();

    const t1Hattricks = matchStats ? countHattricks(t1Id, matchStats) : 0;
    const t2Hattricks = matchStats ? countHattricks(t2Id, matchStats) : 0;
    const hattrickScore = (t1Hattricks + t2Hattricks) * (scoringRules.hattrick ?? 0);

    const teamIds = [t1Id, t2Id];
    const directFkScore = (directFkGoalKeys && matchStats)
      ? countSpecialGoals(teamIds, matchStats, directFkGoalKeys) * (scoringRules.directFk ?? 0)
      : 0;

    const olimpicoScore = (olimpicoGoalKeys && matchStats)
      ? countSpecialGoals(teamIds, matchStats, olimpicoGoalKeys) * (scoringRules.olimpico ?? 0)
      : 0;

    const keeperGoalScore = (keeperGoalKeys && matchStats)
      ? countSpecialGoals(teamIds, matchStats, keeperGoalKeys) * (scoringRules.keeperGoal ?? 0)
      : 0;

    const teamGoals3plusScore = (t1Results.teamGoals3plus + t2Results.teamGoals3plus)
      * (scoringRules['3+goals'] ?? 0);

    const t1WinsMostReds = effectiveMostRedsWinnerIds.has(t1Id);
    const t2WinsMostReds = effectiveMostRedsWinnerIds.has(t2Id);
    const mostRedsBonus = scoringRules.mostReds ?? 0;
    const mostRedsRedCardRefund = Math.abs(scoringRules.redCard);
    const mostRedsScore = (t1WinsMostReds
      ? t1Results.redCards * mostRedsRedCardRefund + mostRedsBonus : 0)
      + (t2WinsMostReds
        ? t2Results.redCards * mostRedsRedCardRefund + mostRedsBonus : 0);

    const winsScore = (t1Results.wins * scoringRules.t1Win) + (t2Results.wins * scoringRules.t2Win);
    const drawsScore = (t1Results.draws * scoringRules.t1Draw)
      + (t2Results.draws * scoringRules.t2Draw);
    const cleanSheetsScore = (t1Results.cleanSheets + t2Results.cleanSheets)
      * scoringRules.cleanSheet;
    const redCardsScore = (t1Results.redCards + t2Results.redCards) * scoringRules.redCard;
    const ownGoalsScore = (t1Results.ownGoals + t2Results.ownGoals) * scoringRules.ownGoal;

    return {
      name: user.name,
      score: t1Score + t2Score,
      winsScore,
      drawsScore,
      cleanSheetsScore,
      redCardsScore,
      ownGoalsScore,
      goldenBoot: goldenBootWinner ? scoringRules.goldenBoot : 0,
      groupWinner: groupWinnerScore,
      earliestGoal: scoredEarliestGoal ? scoringRules.fastestGoal : 0,
      latestGoal: scoredLatestGoal ? scoringRules.latestGoal : 0,
      hattrick: hattrickScore,
      directFk: directFkScore,
      olimpico: olimpicoScore,
      keeperGoal: keeperGoalScore,
      mostReds: mostRedsScore,
      teamGoals3plus: teamGoals3plusScore,
    };
  });
}
